<script setup lang="ts">
import { IConfirmDialogProps } from '@/composables/useConfirmDialog';
import { useMobile } from '@/composables/useMobile';
import NativeDialog from '@/core/dialogsystem/NativeDialog.vue';
import { computed, nextTick, ref, useTemplateRef } from 'vue';
import GlobalConfirmDialogContent from './GlobalConfirmDialogContent.vue';

const confirmDialogRef = useTemplateRef('confirmDialogRef');
const confirmContentRef = useTemplateRef('confirmContentRef');

const options = ref<IConfirmDialogProps | null>(null);
const { isMobile } = useMobile();

const emit = defineEmits<{
  'close:dialog': [];
  'confirm:dialog': [];
  'decline:dialog': [];
}>();

let outcomeEmitted = false;
let nativeClosePromise: Promise<void> | null = null;
let resolveNativeClose: (() => void) | null = null;

const show = async (dialogOptions: IConfirmDialogProps): Promise<void> => {
  // A native close event arrives in a later task. Finish closing the previous
  // dialog before reusing it, so that event cannot dismiss the next confirmation.
  if (nativeClosePromise) await nativeClosePromise;
  options.value = dialogOptions;
  outcomeEmitted = false;
  confirmDialogRef.value?.showModal();
  void nextTick(() => confirmContentRef.value?.resetInput());
};

const hide = (): void => {
  nativeClosePromise = new Promise<void>((resolve) => {
    resolveNativeClose = resolve;
  });
  confirmDialogRef.value?.close();
};

const confirm = (): void => {
  if (outcomeEmitted) return;
  outcomeEmitted = true;
  emit('confirm:dialog');
  hide();
};

const decline = (): void => {
  if (outcomeEmitted) return;
  outcomeEmitted = true;
  emit('decline:dialog');
  hide();
};

const dismiss = (): void => {
  if (outcomeEmitted) return;
  outcomeEmitted = true;
  if (options.value?.declineOnClose === true) emit('decline:dialog');
  else emit('close:dialog');
  hide();
};

const handleNativeClose = (): void => {
  resolveNativeClose?.();
  resolveNativeClose = null;
  nativeClosePromise = null;
  if (outcomeEmitted) {
    outcomeEmitted = false;
    return;
  }
  if (options.value?.declineOnClose === true) emit('decline:dialog');
  else emit('close:dialog');
};

const size = computed(() => options.value?.size ?? 'medium');

defineExpose({
  show,
});
</script>

<template>
  <NativeDialog
    v-if="!isMobile"
    ref="confirmDialogRef"
    :class="['rounded-dialog', 'max-h-[680px]', 'overflow-hidden']"
    :close-on-backdrop-click="false"
    :close-on-cancel="false"
    @cancel="dismiss"
    @close="handleNativeClose"
  >
    <div
      :class="[
        'flex flex-col',
        'max-h-[680px]',
        'shadow-popover',
        'px-content py-xl',
        'w-full max-w-[448px]',
        size === 'lg' && 'max-w-[512px]',
        size === 'xl' && 'max-w-[590px]',
      ]"
    >
      <GlobalConfirmDialogContent
        ref="confirmContentRef"
        :options="options"
        @close="dismiss"
        @confirm="confirm"
        @decline="decline"
      />
    </div>
  </NativeDialog>

  <NativeDialog
    v-else
    ref="confirmDialogRef"
    class="fixed inset-0 m-0 h-dvh max-h-none w-screen max-w-none overflow-visible rounded-none border-0 bg-transparent p-0 outline-none"
    :close-on-backdrop-click="true"
    :close-on-cancel="false"
    @cancel="dismiss"
    @close="handleNativeClose"
  >
    <section
      class="fixed inset-x-0 bottom-0 flex max-h-mobile-drawer-tall flex-col overflow-hidden rounded-t-dialog bg-white px-lg pb-mobile-safe-bottom-lg pt-md shadow-popover"
    >
      <GlobalConfirmDialogContent
        ref="confirmContentRef"
        is-mobile
        :options="options"
        @close="dismiss"
        @confirm="confirm"
        @decline="decline"
      />
    </section>
  </NativeDialog>
</template>
